import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { aboutInfo } from '@/mocks/anime';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError('');

    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      setIsSubmitting(false);
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9sm8814ne17ei0sdk00', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络异常，请检查网络连接后重试');
      setFormStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebPage" data={{ name: '关于奶泡动漫 - 高品质动漫在线观看平台', description: '了解奶泡动漫的品牌故事、核心优势和联系方式' }} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: '关于我们' },
                ]}
              />
            </div>

            <section className="mb-16">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-6 text-center">
                关于奶泡动漫
              </h1>
              <div className="max-w-3xl mx-auto">
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed text-center mb-8">
                  {aboutInfo.brandIntro}
                </p>
                <div className="bg-primary-100/70 rounded-xl p-6 text-center">
                  <p className="text-primary-700 text-sm md:text-base font-medium italic">
                    " {aboutInfo.mission} "
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-16">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                {aboutInfo.achievements.map((item) => (
                  <div key={item.label} className="bg-background-100 rounded-xl p-5 md:p-7 text-center">
                    <div className="text-2xl md:text-3xl font-bold text-primary-500 mb-1">{item.value}</div>
                    <div className="text-xs md:text-sm text-foreground-500">{item.label}</div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-16">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-8 text-center">
                核心优势
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {aboutInfo.features.map((feature) => (
                  <div key={feature.title} className="bg-background-100 rounded-xl p-5 md:p-6 hover:bg-background-200/70 transition-colors">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 mb-4">
                      <i className={`${feature.icon} text-lg`}></i>
                    </div>
                    <h3 className="text-sm md:text-base font-semibold text-foreground-900 mb-2">{feature.title}</h3>
                    <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{feature.description}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-12">
                <div className="lg:col-span-2 space-y-6">
                  <h2 className="text-xl md:text-2xl font-heading text-foreground-900">
                    联系我们
                  </h2>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    如果你有任何问题、建议或合作意向，欢迎通过以下方式联系我们，我们会尽快回复。
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                        <i className="ri-mail-line"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-400">邮箱</p>
                        <p className="text-sm text-foreground-700">{aboutInfo.contact.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-500 flex-shrink-0">
                        <i className="ri-weibo-line"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-400">微博</p>
                        <p className="text-sm text-foreground-700">{aboutInfo.contact.weibo}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-500 flex-shrink-0">
                        <i className="ri-wechat-line"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-400">微信</p>
                        <p className="text-sm text-foreground-700">{aboutInfo.contact.wechat}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                        <i className="ri-group-line"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-400">QQ群</p>
                        <p className="text-sm text-foreground-700">{aboutInfo.contact.qqGroup}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-3">
                  <div className="bg-background-100 rounded-xl p-6 md:p-8">
                    <h3 className="text-base font-semibold text-foreground-900 mb-6">发送消息</h3>

                    {formStatus === 'success' ? (
                      <div className="text-center py-10">
                        <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
                          <i className="ri-check-line text-2xl text-accent-500"></i>
                        </div>
                        <p className="text-foreground-700 font-medium">消息已发送成功！</p>
                        <p className="text-sm text-foreground-500 mt-1">感谢你的来信，我们会尽快回复</p>
                        <button
                          onClick={() => setFormStatus('idle')}
                          className="mt-6 px-5 py-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                        >
                          发送新消息
                        </button>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit} data-readdy-form>
                        {formStatus === 'error' && formError && (
                          <div className="mb-4 p-3 bg-primary-50 text-primary-700 rounded-lg text-sm">
                            {formError}
                          </div>
                        )}
                        <input
                          type="text"
                          name="website_alt"
                          className="honeypot-field"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                        />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label htmlFor="contact-name" className="block text-xs text-foreground-500 mb-1.5">
                              姓名 <span className="text-primary-500">*</span>
                            </label>
                            <input
                              id="contact-name"
                              name="name"
                              type="text"
                              required
                              className="w-full px-3.5 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                              placeholder="你的名字"
                            />
                          </div>
                          <div>
                            <label htmlFor="contact-email" className="block text-xs text-foreground-500 mb-1.5">
                              邮箱 <span className="text-primary-500">*</span>
                            </label>
                            <input
                              id="contact-email"
                              name="email"
                              type="email"
                              required
                              className="w-full px-3.5 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                              placeholder="your@email.com"
                            />
                          </div>
                        </div>
                        <div className="mb-4">
                          <label htmlFor="contact-subject" className="block text-xs text-foreground-500 mb-1.5">
                            主题
                          </label>
                          <input
                            id="contact-subject"
                            name="subject"
                            type="text"
                            className="w-full px-3.5 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                            placeholder="消息主题"
                          />
                        </div>
                        <div className="mb-5">
                          <label htmlFor="contact-message" className="block text-xs text-foreground-500 mb-1.5">
                            消息 <span className="text-primary-500">*</span>
                          </label>
                          <textarea
                            id="contact-message"
                            name="message"
                            required
                            rows={5}
                            maxLength={500}
                            className="w-full px-3.5 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                            placeholder="请输入你想说的话（最多500字）"
                          ></textarea>
                          <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                        </div>
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          {isSubmitting ? '发送中...' : '发送消息'}
                        </button>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}